import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import { CounterpartyAddress } from "@common/schemas/counterpartyAddress.schema";
import { CounterpartyContact } from "@common/schemas/counterpartyContact.schema";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import type { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import {
    CounterpartyBookTableCallbacks,
    CounterpartyBookTableOptions,
} from "../counterpartyBookTableMeta";

type BookTableMeta = ViewTableMeta<
    CounterpartyBookTableCallbacks,
    CounterpartyBookTableOptions
>;

function isBookTableMeta(meta: object | undefined): meta is BookTableMeta {
    return meta !== undefined && "callbacks" in meta && "options" in meta;
}

const BookActions = ({
    id,
    isPrimary,
    meta,
}: {
    id: string;
    isPrimary: boolean;
    meta: BookTableMeta | undefined;
}) => {
    const isReadOnly = meta?.options?.isReadOnly ?? false;
    const isDisabled = meta?.options?.isDisabled ?? false;
    const isRemoved = meta?.options?.removedIds?.includes(id) === true;

    if (isReadOnly || isRemoved) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell captureClicks>
            <Menu.More
                size="sm"
                aria-label="More actions"
                className={viewTableStyles.showOnHover}
                disabled={isDisabled}
            >
                {!isPrimary && (
                    <Menu.Item
                        onClick={() => meta?.callbacks?.onMakePrimary(id)}
                        disabled={isDisabled}
                    >
                        Make primary
                    </Menu.Item>
                )}
                <Menu.Item
                    onClick={() => meta?.callbacks?.onEdit(id)}
                    disabled={isDisabled}
                >
                    Edit
                </Menu.Item>
                <Menu.Item
                    onClick={() => meta?.callbacks?.onDelete(id)}
                    disabled={isDisabled}
                    destructive
                >
                    Delete
                </Menu.Item>
            </Menu.More>
        </ViewTableCell>
    );
};

export const CounterpartyContactActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, CounterpartyContact, string>) => {
        const meta = table.options.meta;
        return (
            <BookActions
                id={row.original.id}
                isPrimary={row.original.isPrimary}
                meta={isBookTableMeta(meta) ? meta : undefined}
            />
        );
    },
);

export const CounterpartyAddressActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, CounterpartyAddress, string>) => {
        const meta = table.options.meta;
        return (
            <BookActions
                id={row.original.id}
                isPrimary={row.original.isPrimary}
                meta={isBookTableMeta(meta) ? meta : undefined}
            />
        );
    },
);

CounterpartyContactActionsCell.displayName = "CounterpartyContactActionsCell";
CounterpartyAddressActionsCell.displayName = "CounterpartyAddressActionsCell";
